import { replaceMaterialSnippets } from '#imports';
import { AdditiveBlending, Sprite, SpriteMaterial } from 'three';

import glslRotate from '@/libs/lygia/space/rotate.glsl';

const FLARE_DEFAULTS = {
    uFlareRayWidth: 0.0175,
    uFlareRayFade: 0.2,
    uFlareRayIntensity: 1,
    uFlareAlpha: 0,
    uFlareRotation: 0,
};

/**
 * Procedural “star + glow” lens flare for a `Sprite` (same shader as shine-wipe’s flares).
 * Additive, no depth so it layers over the scene; pair with a parent `Object3D` for layout.
 */
export function createShineFlareSpriteMaterial(overrides = {}) {
    const u = { ...FLARE_DEFAULTS, ...overrides };
    const material = new SpriteMaterial({
        depthWrite: false,
        depthTest: false,
        blending: AdditiveBlending,
    });
    replaceMaterialSnippets({
        material,
        uniforms: {
            uFlareRayWidth: { value: u.uFlareRayWidth },
            uFlareRayFade: { value: u.uFlareRayFade },
            uFlareRayIntensity: { value: u.uFlareRayIntensity },
            uFlareAlpha: { value: u.uFlareAlpha },
            uFlareRotation: { value: u.uFlareRotation },
        },
        vertex: [
            {
                snippet: '#include <common>',
                newSnippet: /* glsl */ `varying vec2 vUv;`,
            },
            {
                snippet: '#include <uv_vertex>',
                newSnippet: /* glsl */ `vUv = uv;`,
            },
        ],
        fragment: [
            {
                snippet: '#include <common>',
                newSnippet: /* glsl */ `
                uniform float uFlareRayWidth;
                uniform float uFlareRayFade;
                uniform float uFlareRayIntensity;
                uniform float uFlareAlpha;
                uniform float uFlareRotation;
                varying vec2 vUv;

                ${glslRotate}
            `,
            },
            {
                snippet: '#include <fog_fragment>',
                newSnippet: /* glsl */ `
                vec2 relUv = rotate(vUv, uFlareRotation) - vec2(0.5);

                float dist = length(relUv);
                float glow = 0.05 / dist;
                glow = clamp(glow, 0.0, 1.2);
                glow *= smoothstep(0.5, 0.0, distance(relUv, vec2(0.0)));

                float rayWidth = uFlareRayWidth;
                float rayFade = uFlareRayFade;

                float vRay = smoothstep(rayWidth, 0.0, abs(relUv.x)) * smoothstep(rayFade, 0.0, abs(relUv.y));
                float hRay = smoothstep(rayWidth, 0.0, abs(relUv.y)) * smoothstep(rayFade, 0.0, abs(relUv.x));

                vec3 flare = vec3(glow) + (vec3(vRay + hRay) * uFlareRayIntensity);

                float a = flare.r * uFlareAlpha;
                gl_FragColor = vec4(vec3(1.0), a);
            `,
            },
        ],
    });
    return material;
}

/**
 * @param {import('three').SpriteMaterial} material
 * @param {object} p
 * @param {number} p.uFlareRayWidth
 * @param {number} p.uFlareRayFade
 * @param {number} p.uFlareRayIntensity
 * @param {number} p.uFlareAlpha
 * @param {number} p.uFlareRotation
 */
export const syncShineFlareMaterialUniforms = (material, p) => {
    if (!material?.uniforms) return;
    const u = material.uniforms;
    if (u.uFlareRayWidth) u.uFlareRayWidth.value = p.uFlareRayWidth;
    if (u.uFlareRayFade) u.uFlareRayFade.value = p.uFlareRayFade;
    if (u.uFlareRayIntensity) u.uFlareRayIntensity.value = p.uFlareRayIntensity;
    if (u.uFlareAlpha) u.uFlareAlpha.value = p.uFlareAlpha;
    if (u.uFlareRotation) u.uFlareRotation.value = p.uFlareRotation;
};

/** @param {import('three').Object3D} [parent] */
export function createShineFlareSprite({ material, parent, scale = 0.5, renderOrder = 2 } = {}) {
    const mesh = new Sprite(material);
    mesh.scale.setScalar(scale);
    mesh.renderOrder = renderOrder;
    if (parent) parent.add(mesh);
    return mesh;
}
